/**
 * FAQ sub-page — rendered at `?tab=more&view=faqs` inside MorePage.
 * Reuses FAQBody from FAQBottomSheet for categories + accordion list.
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { getTranslatedValue, type NameTranslations } from "@wondermaps/db/client";
import type { FAQConfig } from "@wondermaps/db/schema";
import { VisitorPageHeader } from "./VisitorPageHeader";
import { FAQBody } from "./FAQBottomSheet";
import type { VisitorNotification } from "./notification-types";
import type { VisitorSearchParams } from "../../lib/visitor-search";

export interface FaqSubPageProps {
  notifications: VisitorNotification[];
  onBellClick: () => void;
  isTrayOpen: boolean;
  mapSlug: string;
  faqConfig?: FAQConfig;
}

export function FaqSubPage({ notifications, onBellClick, isTrayOpen, mapSlug, faqConfig }: FaqSubPageProps) {
  const { t, i18n } = useTranslation("map");
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VisitorSearchParams;

  const title = getTranslatedValue(
    faqConfig?.title || t("more.faqsTitle"),
    (faqConfig?.titleTranslations ?? null) as NameTranslations,
    i18n.language,
  );

  const handleBack = () => {
    void navigate({ to: ".", search: { ...search, view: undefined } });
  };

  const hasFAQ =
    !!faqConfig?.enabled && (faqConfig.items?.length ?? 0) > 0;

  return (
    <div
      className="absolute inset-0 z-10 bg-background overflow-y-auto"
      style={{ paddingBottom: "var(--bottom-nav-height, 0px)" }}
    >
      <VisitorPageHeader
        showBack
        onBack={handleBack}
        title={title}
        notifications={notifications}
        onBellClick={onBellClick}
        isTrayOpen={isTrayOpen}
        mapSlug={mapSlug}
      />
      {hasFAQ ? (
        <FAQBody faqConfig={faqConfig!} lang={i18n.language} />
      ) : (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t("faq.empty", { defaultValue: "No FAQs yet." })}
        </div>
      )}
    </div>
  );
}
